import { isEqual, noop } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
  PropsWithChildren,
  useRef,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { DEFAULT_DEBOUNCE_RATE, ROUTES } from '../../Base.constants';
import { Filters, FilterRange } from '../../models';
import useDidUpdate from '../common/useDidUpdate';
import {
  parseFiltersFromSearchParams,
  updateInvalidFilters,
  validateFilters,
  addIssuersSearchParams,
  mergeFiltersState,
} from './useIssuersFilterContext.logic';
import { trackIssuersCoverageFilter } from '../../utils/trackUsageUtils';

const initialFilters: Filters = {
  tags: {},
  recWeightings: [],
  ranges: {},
  coverage: [],
  coverageOperation: 'or',
  searchText: '',
};

const IssuersFilterContext = createContext<IssuersFilterOptions>({
  activeFilters: initialFilters,
  touchedFilters: [],
  setTags: noop,
  setRange: noop,
  setRecWeightings: noop,
  setSearchText: noop,
  toggleCoverage: noop,
  setWatchlistId: noop,
  resetFilters: noop,
  setCoverageOperation: noop,
  setCoverage: noop,
});

interface IssuersFilterOptions {
  activeFilters: Filters;
  touchedFilters: (keyof Filters)[];
  setTags: (type: string, tags: string[]) => void;
  setRange: (type: string, range: FilterRange) => void;
  setRecWeightings: (recWeightings: string[]) => void;
  setSearchText: (text: string) => void;
  toggleCoverage: (item: string) => void;
  setWatchlistId: (watchlistId: string) => void;
  resetFilters: () => void;
  setCoverageOperation: (type: Filters['coverageOperation']) => void;
  setCoverage: (items: string[]) => void;
}

export const IssuersFilterContextProvider = ({ children }: PropsWithChildren): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(parseFiltersFromSearchParams(searchParams));
  const [touchedFilters, setTouchedFilters] = useState<(keyof Filters)[]>([]);
  const isFilterValid = useMemo(() => validateFilters(filters), [filters]);
  const applyParamsPending = useRef(false);

  const setFiltersAndParams: typeof setFilters = useCallback((payload) => {
    setFilters(payload);
    applyParamsPending.current = true;
  }, []);

  const onFiltersChange = useCallback(<T extends keyof Filters>(key: T, nextPartial: Filters[T]) => {
    setTouchedFilters((prev) => {
      if (!prev.includes(key)) {
        return [...prev, key];
      }

      return prev;
    });
    setFiltersAndParams((prevFilters) => mergeFiltersState(prevFilters || initialFilters, key, nextPartial));
  }, []);

  const applySearchParams = useCallback(
    (nextFilters: Filters) => {
      const params = addIssuersSearchParams(searchParams, nextFilters);

      if (params.toString() !== searchParams.toString()) {
        setSearchParams(params);
      }

      applyParamsPending.current = false;
    },
    [searchParams, filters]
  );

  const applySearchParamsDebounced = useDebouncedCallback(applySearchParams, DEFAULT_DEBOUNCE_RATE);

  const setTags: IssuersFilterOptions['setTags'] = useCallback((type, tags) => {
    onFiltersChange('tags', { [type]: tags });
  }, []);

  const setRange: IssuersFilterOptions['setRange'] = useCallback((type, range) => {
    onFiltersChange('ranges', { [type]: range });
  }, []);

  const setRecWeightings: IssuersFilterOptions['setRecWeightings'] = useCallback((recWeightings) => {
    onFiltersChange('recWeightings', recWeightings);
  }, []);

  const toggleCoverage: IssuersFilterOptions['toggleCoverage'] = useCallback(
    (item) => {
      const stateCoverage = filters?.coverage || [];
      const coverage = stateCoverage.includes(item)
        ? stateCoverage.filter((c) => c !== item)
        : [...stateCoverage, item];
      trackIssuersCoverageFilter(item, coverage);

      onFiltersChange('coverage', coverage);
    },
    [filters]
  );

  const setCoverage = useCallback(
    (items: string[]) => {
      onFiltersChange('coverage', items);
    },
    [onFiltersChange]
  );

  const setSearchText: IssuersFilterOptions['setSearchText'] = useCallback((text) => {
    onFiltersChange('searchText', text);
  }, []);

  const setWatchlistId: IssuersFilterOptions['setWatchlistId'] = useCallback((watchlistId) => {
    onFiltersChange('watchlistId', watchlistId);
  }, []);

  const resetFilters: IssuersFilterOptions['resetFilters'] = useCallback(() => {
    setFilters(initialFilters);
    setTouchedFilters([]);
    applyParamsPending.current = false;
    navigate(ROUTES.companies());
  }, []);

  const setCoverageOperation: IssuersFilterOptions['setCoverageOperation'] = useCallback(
    (type) => {
      onFiltersChange('coverageOperation', type);
    },
    [onFiltersChange]
  );

  useEffect(() => {
    const paramsFilters = parseFiltersFromSearchParams(searchParams);
    const { searchText, ranges: rawRanges, ...filtersWithoutRanges } = filters;

    const ranges = rawRanges
      ? Object.entries(rawRanges).reduce(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (acc: typeof rawRanges, [key, { isFractional: _, ...range }]) => ({
            ...acc,
            [key]: range,
          }),
          {}
        )
      : rawRanges;

    const filtersToCompare =
      searchText == null ? filters : { ...filtersWithoutRanges, ranges, searchText: searchText.trim() };

    if (!isEqual(filtersToCompare, paramsFilters)) {
      setFilters(paramsFilters);
      setTouchedFilters([]);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isFilterValid) {
      setFiltersAndParams(updateInvalidFilters(filters));
    }
  }, [isFilterValid]);

  useDidUpdate(() => {
    if (applyParamsPending.current) {
      applySearchParamsDebounced(filters);
    }
  }, [filters]);

  const value: IssuersFilterOptions = useMemo(
    () => ({
      activeFilters: filters || initialFilters,
      touchedFilters,
      setRecWeightings,
      setTags,
      setRange,
      setSearchText,
      toggleCoverage,
      setWatchlistId,
      resetFilters,
      setCoverageOperation,
      setCoverage,
    }),
    [filters]
  );

  return <IssuersFilterContext.Provider value={value}>{children}</IssuersFilterContext.Provider>;
};

export default (): IssuersFilterOptions => useContext(IssuersFilterContext);
